import { isNullOrUndefined, isUndefined } from './typeChecks';
import { IMergeOptions } from '../models/common/IMergeOptions';
import { isArrayLike, isObservableArray } from './isArrayLike';
const OPEN_CHAR = '{';
const CLOSE_CHAR = '}';
const MODIFIER_SEPARATOR = ':';

export function safe<TResult>(action: () => TResult | undefined, defaultResult?: TResult): TResult | undefined {
	try {
		const result = action();
		return !isUndefined(result) ? result : defaultResult;
	}
	catch (e) {
		if (e.name === 'TypeError' || e.name === 'ReferenceError') {
			return defaultResult;
		}
		throw e;
	}
}

/**
 * Will get the value specified in the string notation from the instance that is passed to it.
 *
 * @export
 * @param {(string |)} [stringNotation=null | undefined]
 * @param {({} |)} [instance=null | undefined]
 * @param {*} [defaultValue=null]
 * @returns {*}
 */
export function getValueFromStringNotation(stringNotation: string | null | undefined, instance: {} | null | undefined, defaultValue?: any): any {
	let value = null;
	const parameterList = getInnerParameters(stringNotation);
	if (parameterList && parameterList.length > 0) {
		const parameters = {};
		for (const parameter of parameterList) {
			const parameterValue = getValueFromDotNotation(parameter, instance, defaultValue);
			if (parameterValue) {
				parameters[parameter] = parameterValue;
			}
		}
		value = stringNotation;
		for (const parameter of Object.keys(parameters)) {
			const fullParameter = `${OPEN_CHAR}${parameter}${CLOSE_CHAR}`;
			value = value.split(fullParameter).join(parameters[parameter]);
		}
	}
	else {
		value = getValueFromDotNotation(stringNotation, instance, defaultValue);
	}


	if (isNullOrUndefined(value)) {
		value = defaultValue;
	}

	return value;
}
/**
 * This will get the value from the property for a complex identifier (If the source field is deeper than the base object it will traverse)
 *
 * @export
 * @param {(string |)} [sourceField=null | undefined]
 * @param {({} |)} [instance=null | undefined]
 * @param {*} [defaultValue=null]
 * @returns {*}
 */
export function getValueFromDotNotation(sourceField: string | null | undefined, instance: {} | null | undefined, defaultValue: any = null): any {
	let currentObj = instance;
	let value = defaultValue;
	if (!isNullOrUndefined(sourceField)) {
		let modifierDetails = null;
		if (sourceField.indexOf(MODIFIER_SEPARATOR) > -1) {
			modifierDetails = sourceField.split(MODIFIER_SEPARATOR);
			sourceField = modifierDetails.shift();
		}
		const sourceFieldSplit = sourceField.split('.');
		while (sourceFieldSplit.length > 0) {
			if (!currentObj) {
				break;
			}
			const attr = <string>sourceFieldSplit.shift();
			if (!isNullOrUndefined(attr)) {
				if (sourceFieldSplit.length > 0) {
					currentObj = safe(() => currentObj[attr], null);
				}
				else {
					value = safe(() => currentObj[attr], defaultValue);
				}
			}
		}
		if (modifierDetails) {
			value = applyModifier(value, modifierDetails);
		}
	}
	return value;
}

/**
 * Will take in the original value and details on how to modify it. This allows for uppercase, lowercase etc
 *
 * @export
 * @param {*} value
 * @param {string[]} [modifierDetails=[]]
 * @returns {*}
 */
export function applyModifier(value: any, modifierDetails: string[] = []): any {
	if (modifierDetails && modifierDetails.length > 0 && !isNullOrUndefined(value) && typeof value !== 'object') {
		const firstModifier = modifierDetails[0];
		// Upper case
		if (firstModifier === 'U') {
			value = value.toString().toUpperCase();
		}
		if (firstModifier === 'L') {
			value = value.toString().toLowerCase();
		}
	}
	return value;
}

/**
 * Fetches parameters that are inside the string that are wrapped with the opening and closing tags ({})
 *
 * @export
 * @param {string} stringNotation
 * @returns {string[]}
 */
export function getInnerParameters(stringNotation: string): string[] {
	const result = [];
	const workingNotation = stringNotation;
	let isOpen = false;
	let currentParameter = '';
	for (const char of workingNotation.split('')) {
		if (char === OPEN_CHAR) {
			if (!isOpen) {
				currentParameter = '';
				isOpen = true;
			}
			else {
				throw new Error('object: getInnerParameters: Nested parameters not supported in string notation');
			}
		}
		else if (char === CLOSE_CHAR) {
			if (isOpen) {
				isOpen = false;
				result.push(currentParameter);
				currentParameter = '';
			}
			else {
				throw new Error('object: getInnerParameters: No opening tag found for parameter');
			}
		}
		else if (isOpen) {
			currentParameter += char;
		}
	}
	if (isOpen) {
		throw new Error(`object: getInnerParameters: No closing tag found for parameter (${currentParameter})`);
	}
	return result;
}

/**
 * Will return a merged object
 *
 * @export
 * @param {any[]} elements
 * @returns {any}
 */
export function deepMergeObjects(...elements: any[]): any {
	return deepMergeObjectsWithOptions(null, ...elements);
}

/**
 * Will return a merged object, based on options
 *
 * @export
 * @param {any[]} elements
 * @returns {any}
 */
export function deepMergeObjectsWithOptions(options?: IMergeOptions, ...elements: any[]): any {
	const mergeOptions: IMergeOptions = Object.assign({ arrayMergeStrategy: 'DeepCopy' }, options);
	elements = elements.filter(e => e);
	if (elements.length < 1) {
		console.warn('Need 1 or more elements to merge');
		return;
	}
	let workingElement: any = {};
	const elementLength = elements.length;
	for (let i = 0; i < elementLength; i++) {
		const element = elements[i];
		if (element && Object.keys(element).length > 0) {
			workingElement = mergeRecursive(mergeOptions, workingElement, element);
		}
	}
	return workingElement;
}

function copyIsObject(obj: any): boolean {
	return (!!obj) && (obj.constructor === Object) && !isObservableArray(obj);
}

function deepcopy(instance: any): any {
	if (isArrayLike(instance)) {
		return instance.map(item => deepcopy(item));
	}
	if (copyIsObject(instance)) {
		const response = {};
		for (const key of Object.keys(instance)) {
			response[key] = deepcopy(instance[key]);
		}
		return response;
	}
	return instance;
}

function mergeRecursive(options: IMergeOptions, obj1: any, obj2: any): any {
	const keys = Object.keys(obj2);
	const keysLength = keys.length;
	for (let i = 0; i < keysLength; i++) {
		const key = keys[i];
		try {
			// Property in destination object set; update its value.
			if (isArrayLike(obj2[key])) {
				if (options.arrayMergeStrategy === 'DeepMerge') {
					for (let j = 0; j < obj2[key].length; j++) {
						if (!obj1[key][j]) {
							obj1[key][j] = obj2[key][j];
						}
						else {
							obj1[key][j] = mergeRecursive(options, obj1[key][j], obj2[key][j]);
						}
					}
				}
				else if (options.arrayMergeStrategy === 'DeepCopy') {
					obj1[key] = obj2[key].map(item => deepcopy(item));
				}
				else if (options.arrayMergeStrategy === 'AppendDeepCopy') {
					if (!obj1[key]) {
						obj1[key] = [];
					}
					for (let j = 0; j < obj2[key].length; j++) {
						obj1[key].push(deepcopy(obj2[key][j]));
					}					
				}
				else if (options.arrayMergeStrategy === 'DeepMergeByKeyOrAppend') {
					if (!obj1[key]) {
						obj1[key] = [];
					}
					const itemsToPush = [];
					for (let j = 0; j < obj2[key].length; j++) {
						const obj2Key = options.getKeyFn(obj2[key][j]);
						let merged = false;
						obj1[key].forEach((item, index) => {
							const obj1Key = options.getKeyFn(item);
							if (obj1Key === obj2Key) {
								obj1[key][index] = mergeRecursive(options, obj1[key][index], obj2[key][j]);
								merged = true;
							}
						});
						if (!merged) {
							itemsToPush.push(obj2[key][j]);
						}
					}
					obj1[key].push(...itemsToPush);
				}
				else {
					obj1[key] = obj2[key];
				}
			}
			else if (copyIsObject(obj2[key])) {
				obj1[key] = mergeRecursive(options, obj1[key] || {}, obj2[key]);
			}
			else {
				obj1[key] = obj2[key];
			}

		}
		catch (e) {
			// Property in destination object not set; create it and set its value.
			obj1[key] = obj2[key];
		}
	}
	return obj1;
}
